import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorMessage, Field, FormikProvider, useFormik } from 'formik';
import { Grid, Divider, Select, MenuItem, SelectProps } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import * as Yup from 'yup';
import { setCrest } from '../../../store/project/projectslice';
import './Crest.scss';
import { ICrestProps } from './ICrest';
import { useEffect } from 'react';
import { conversionLabel } from '../../../../conversion';
import { selectProjectInfo, selectMacro2 } from '../../../../store/projectInfo/projectInfoSlice';
import Dialog from '../../../../../components/Dialog/Dialog';
import anchorSpacing from '../../../../images/macro2/Macro2_anchor_spacing_x.png';
import rockGrout from '../../../../images/macro2/Macro2_rock_grout.png';
import rockGroutImp from '../../../../images/macro2/Macro2_rock_grout_imperiale.png';
import assumedPlasticization from '../../../../images/macro2/Macro2_assumed_plasticization.png';
import { updateValidation } from '../../../../Macro2/store/project/resultsSlice';
import { RootState } from '../../../../../reduxService';
import { getRopesTypes, getRopeSteels, getRopeCores, getAnchorTypes, getCrestRopeSpecification } from '../../../../api/products/BeMaccaferriProductsHelper';
import { IRopesResponse, IRopeSteelsResponse, IRopeCoreTypesResponse, IAnchorTypesResponse } from '../../../../api/IApiResponse';
import FieldNumber from '../../../../../components/FieldNumber/FieldNumber';

export interface ICrestForm {
    horizontalAnchorSpacing: number;
    //verticalAnchorSpacing: number;
    customRope: boolean;
    ropeDiameter: string;
    ropeDiameterId: number;
    ropeSteelGrade: number;
    ropeSteelGradeId: number;
    ropeCoreType: number;
    ropeCoreTypeName: string;
    ropeUltimateTensileStrength: number;
    customAnchor: boolean;
    anchorType: number;
    anchorTypeName: string;
    anchorInternalDiameter: number;
    anchorExternalDiameter: string;
    drillingDiameter: number;
    automaticCrownThickness: boolean; // If true automatically calculate the thickness
    crownThickness: number;
    environmentalAggressivity: number; // If automaticThickness is selected, environmentalAggressivity is selected (low, medium, hard)
    anchoryieldStress: number;
    lifespanYears: number; // If automaticThickness is selected, lifespanYears is selected
    NFP94270: boolean;
    rockGroutAdhesion: number;
    assumedPlasticizationBetweenRockAndAnchor: number;
}

export const CrestDefault: ICrestForm = {
    horizontalAnchorSpacing: 3,
    //verticalAnchorSpacing: 0,
    customRope: false,
    ropeDiameter: '16.00',
    ropeDiameterId: 0,
    ropeSteelGrade: 1770,
    ropeSteelGradeId: 0,
    ropeCoreType: 0,
    ropeCoreTypeName: 'Steel',
    ropeUltimateTensileStrength: 0,
    customAnchor: false,
    anchorType: 0,
    anchorTypeName: '',
    anchorInternalDiameter: 0,
    anchorExternalDiameter: '16.00',
    drillingDiameter: 40,
    automaticCrownThickness: false,
    crownThickness: 0,
    environmentalAggressivity: 37.5,
    lifespanYears: 50,
    anchoryieldStress: 0,
    NFP94270: false,
    rockGroutAdhesion: 0.3,
    assumedPlasticizationBetweenRockAndAnchor: 0.3,
};

const MuiSelect = (props: SelectProps) => <Select {...props} />;

const Crest = (props: ICrestProps) => {
    const { t } = useTranslation();
    const projectInfo = useAppSelector(selectProjectInfo);
    const macro2 = useAppSelector(selectMacro2);
    const [ErrorDialog, setErrorDialog] = React.useState<string>('');
    const closeErrorDialog = () => {
        setErrorDialog('');
    };

    const [CurrentDialog, setCurrentDialog] = React.useState<string>('');
    const openDialog = (dialogCode: string) => {
        setCurrentDialog(dialogCode);
    };
    const closeDialog = () => {
        setCurrentDialog('');
    };

    const isMetric = projectInfo.data?.unit == 'Metric';

    const rockGroutSrc = isMetric ? rockGrout : rockGroutImp;

    const CrestSchema = Yup.object().shape({
        horizontalAnchorSpacing: Yup.number()
            .min(0.01)
            .required()
            .label(t('crest_horizontal_spacing', { unit_measure: '' })),
        //verticalAnchorSpacing: Yup.number().min(0).required().label(t('crest_vertical_offset', { unit_measure: '' })),
        ropeDiameter: Yup.number()
            .min(0)
            .required()
            .label(t('crest_rope_diameter', { unit_measure: '' })),
        ropeSteelGrade: Yup.number()
            .min(0)
            .required()
            .label(t('crest_rope_steel_grade', { unit_measure: '' })),
        ropeCoreType: Yup.number()
            .required()
            .label(t('crest_rope_core_type', { unit_measure: '' })),
        ropeCoreTypeName: Yup.string(),
        ropeUltimateTensileStrength: Yup.number()
            .min(0.01)
            .required()
            .label(t('crest_rope_tensile_strength', { unit_measure: '' })),
        customAnchor: Yup.boolean().label(t('crest_anchor_custom', { unit_measure: '' })),
        anchorType: Yup.number().label(t('crest_anchor_type', { unit_measure: '' })),
        anchorTypeName: Yup.string().label(t('crest_anchor_type', { unit_measure: '' })),
        anchorInternalDiameter: Yup.number()
            .min(0)
            .required()
            .label(t('crest_inner_diameter', { unit_measure: '' })),
        anchorExternalDiameter: Yup.number()
            .min(0)
            .required()
            .label(t('crest_external_diameter', { unit_measure: '' })),
        drillingDiameter: Yup.number()
            .min(0)
            .required()
            .label(t('crest_drilling_diameter', { unit_measure: '' })),
        automaticCrownThickness: Yup.boolean()
            .required()
            .label(t('crest_automatic_thickness', { unit_measure: '' })),
        crownThickness: Yup.number()
            .min(0)
            .required()
            .label(t('crest_automatic_thickness', { unit_measure: '' })),
        environmentalAggressivity: Yup.number()
            .min(0)
            .required()
            .label(t('crest_environmental_aggressivity', { unit_measure: '' })),
        anchoryieldStress: Yup.number()
            .min(0)
            .required()
            .label(t('crest_anchor_yield_stress', { unit_measure: '' })),
        lifespanYears: Yup.number()
            .min(0)
            .required()
            .label(t('crest_lifespan', { unit_measure: '' })),
        NFP94270: Yup.boolean()
            .required()
            .label(t('crest_anchor_NFP94270', { unit_measure: '' })),
        rockGroutAdhesion: Yup.number()
            .min(0)
            .required()
            .label(t('crest_rock_grout_adhesion', { unit_measure: '' })),
        assumedPlasticizationBetweenRockAndAnchor: Yup.number()
            .min(0)
            .required()
            .label(t('crest_assumed_plasticization', { unit_measure: '' })),
    });

    const dispatch = useAppDispatch();
    const dispatchCrest = (values: ICrestForm) => {
        dispatch(setCrest(values));
    };

    const [selectedCorrosionCrown, setSelectedValue] = React.useState('a');

    const getMeasurementUnit = (unit: string[]) => {
        return ' [' + unit[projectInfo.data?.unit == 'Metric' ? 0 : 1] + ']';
    };

    const formikProps = useFormik({
        initialValues: macro2.project?.data?.crest ?? CrestDefault,
        validationSchema: CrestSchema,
        onSubmit: (values, { setSubmitting }) => {
            setCrest(values);
            dispatchCrest(values);
            setSubmitting(false);
        },
    });

    const [RopesTypes, setRopesTypes] = React.useState<IRopesResponse[]>([]);
    const [RopeSteelsTypes, setRopeSteelsTypes] = React.useState<IRopeSteelsResponse[]>([]);
    const [RopeCoresTypes, setRopeCoresTypes] = React.useState<IRopeCoreTypesResponse[]>([]);
    const [AnchorTypes, setAnchorTypes] = React.useState<IAnchorTypesResponse[]>([]);
    const [AnchorExternalDiameters, setAnchorExternalDiameters] = React.useState<string[]>([]);
    useEffect(() => {
        // React advises to declare the async function directly inside useEffect
        async function getRopesAsync() {
            if (!!projectInfo.data?.unit) {
                try {
                    const respData = (await getRopesTypes()).data;
                    setRopesTypes(
                        respData.map(el => {
                            return { id: el.id, diameter: Number(el.diameter).toFixed(2) };
                        }),
                    );
                    const defRope = respData.find(el => el.diameter == 16) ?? respData[0];
                    if (!formikProps.values.customRope && formikProps.values.ropeDiameterId == 0) {
                        formikProps.setFieldValue('ropeDiameterId', defRope.id);
                        formikProps.setFieldValue('ropeDiameter', defRope.diameter);
                    }
                } catch (ex) {
                    console.error(ex);
                    setErrorDialog('' + t('errors.generic.message'));
                }
            }
        }

        async function getRopeSteelsAsync() {
            if (!!projectInfo.data?.unit) {
                try {
                    const respData = (await getRopeSteels()).data;
                    setRopeSteelsTypes(respData);
                    const defSteelType = respData.find(el => el.grade == 1770) ?? respData[0];
                    if (!formikProps.values.customRope && formikProps.values.ropeSteelGradeId == 0) {
                        formikProps.setFieldValue('ropeSteelGradeId', defSteelType.id);
                        formikProps.setFieldValue('ropeSteelGrade', defSteelType.grade);
                    }
                } catch (ex) {
                    console.error(ex);
                    setErrorDialog('' + t('errors.generic.message'));
                }
            }
        }

        async function getRopeCoresAsync() {
            if (!!projectInfo.data?.unit) {
                try {
                    const respData = (await getRopeCores()).data;
                    setRopeCoresTypes(respData);
                    const defRopeCore = respData.find(el => el.typeName == 'Steel') ?? respData[0];
                    if (!formikProps.values.customRope && formikProps.values.ropeCoreType == 0) {
                        formikProps.setFieldValue('ropeCoreType', defRopeCore.id);
                        formikProps.setFieldValue('ropeCoreTypeName', defRopeCore.typeName);
                    }
                } catch (ex) {
                    console.error(ex);
                    setErrorDialog('' + t('errors.generic.message'));
                }
            }
        }

        async function getAnchorTypesAsync() {
            if (!!projectInfo.data?.unit) {
                try {
                    const respData = (await getAnchorTypes()).data;
                    setAnchorTypes(respData);
                    if (!formikProps.values.customAnchor) {
                        const defId = (respData.find(mt => mt.id == formikProps.values.anchorType) ?? respData.find(el => el.typeName == 'Steel bars B450C') ?? respData[0]).id;
                        formikProps.setFieldValue('anchorType', defId);
                        setNewAnchorType(defId, respData);
                    }
                } catch (ex) {
                    console.error(ex);
                    setErrorDialog('' + t('errors.generic.message'));
                }
            }
        }

        let recalculateRopeTensileStrength: boolean = false;
        if (!RopesTypes || RopesTypes.length == 0) {
            getRopesAsync();
        }
        if (!RopeSteelsTypes || RopeSteelsTypes.length == 0) {
            getRopeSteelsAsync();
        }
        if (!RopeCoresTypes || RopeCoresTypes.length == 0) {
            getRopeCoresAsync();
        }
        if (!AnchorTypes || AnchorTypes.length == 0) {
            getAnchorTypesAsync();
        }
    }, [projectInfo.data?.unit, window.localStorage.getItem('i18nextLng')]);

    const setNewAnchorType = (id: number, forceList?: IAnchorTypesResponse[]) => {
        const newAT = (forceList ?? AnchorTypes).filter(mt => mt.id == id)[0] ?? (forceList ?? AnchorTypes).find(el => el.typeName == 'Steel bars B450C') ?? (forceList ?? AnchorTypes)[0];
        console.log(newAT);
        let anchorTypeName = newAT.typeName;
        let anchorInternalDiameter = newAT.internalDiameter;
        let anchorExternalDiameter = newAT.externalDiameter.includes(Number(formikProps.values.anchorExternalDiameter))
            ? formikProps.values.anchorExternalDiameter
            : newAT.externalDiameter[0].toFixed(2);
        let anchoryieldStress = newAT.yieldStressOfSteel;

        setAnchorExternalDiameters(newAT.externalDiameter.map(el => el.toFixed(2)));

        formikProps.setFieldValue('anchorTypeName', anchorTypeName);
        formikProps.setFieldValue('anchorInternalDiameter', anchorInternalDiameter);
        formikProps.setFieldValue('anchorExternalDiameter', anchorExternalDiameter);
        formikProps.setFieldValue('anchoryieldStress', anchoryieldStress);
        dispatchCrest(formikProps.values);
    };

    const calculateCrestRopeTensileStrength = async (diameterId: number, steelGradeId: number, coreType: number, silent?: boolean) => {
        try {
            const respData = (await getCrestRopeSpecification({ ropeId: diameterId, ropeSteelId: steelGradeId, ropeCoreTypeId: coreType })).data;
            formikProps.setFieldValue('ropeUltimateTensileStrength', respData?.ultimateTensileStrength ?? 0);
        } catch (ex) {
            console.error(ex);
            if (!silent) {
                setErrorDialog('' + t('errors.generic.message'));
            }
        }
    };

    const updateRopeValues = (diameterId: number, steelGradeId: number, coreType: number) => {
        const diameterObj = RopesTypes.find(mt => mt.id == diameterId) ?? RopesTypes[0];
        const steelGradeObj = RopeSteelsTypes.find(mt => mt.id == steelGradeId) ?? RopeSteelsTypes[0];
        const coreTypeObj = RopeCoresTypes.find(mt => mt.id == coreType) ?? RopeCoresTypes[0];
        if (diameterObj) {
            formikProps.setFieldValue('ropeDiameter', diameterObj.diameter);
            formikProps.setFieldValue('ropeDiameterId', diameterObj.id);
        }
        if (steelGradeObj) {
            formikProps.setFieldValue('ropeSteelGradeId', steelGradeObj.id);
            formikProps.setFieldValue('ropeSteelGrade', steelGradeObj.grade);
        }
        if (coreTypeObj) {
            formikProps.setFieldValue('ropeCoreType', coreTypeObj.id);
            formikProps.setFieldValue('ropeCoreTypeName', coreTypeObj.typeName);
        }
        return { diameterObj, steelGradeObj, coreTypeObj };
    };

    const handleValueChanged = (valueKey: string, value: any) => {
        formikProps.setFieldValue(valueKey, value);
        switch (valueKey) {
            case 'anchorType':
                setNewAnchorType(value);
                break;
            case 'customAnchor':
                if (!value) {
                    setNewAnchorType(formikProps.values.anchorType);
                }
                break;
        }
        dispatchCrest(formikProps.values);
    };

    useEffect(() => {
        if (!formikProps.values.customRope && formikProps.values.ropeDiameterId > 0 && formikProps.values.ropeSteelGradeId > 0 && formikProps.values.ropeCoreType > 0) {
            const newVals = updateRopeValues(formikProps.values.ropeDiameterId, formikProps.values.ropeSteelGradeId, formikProps.values.ropeCoreType);
            //console.log("updateRope", newVals);
            calculateCrestRopeTensileStrength(formikProps.values.ropeDiameterId, formikProps.values.ropeSteelGradeId, formikProps.values.ropeCoreType, true);
        }
    }, [formikProps.values.ropeDiameterId, formikProps.values.ropeSteelGradeId, formikProps.values.ropeCoreType, formikProps.values.customRope]);

    useEffect(() => {
        dispatchCrest(formikProps.values);
    }, [formikProps.values]);

    const currentResults = useAppSelector((state: RootState) => {
        return { ...state.Macro2.results.data };
    });
    useEffect(() => {
        if (currentResults.validation?.startValidation && !currentResults.validation?.crest) {
            formikProps.submitForm();
            formikProps.validateForm().then(errors => {
                if (currentResults.validation)
                    dispatch(
                        updateValidation({
                            ...currentResults.validation,
                            crest: true,
                            crestErrors: Object.keys(errors).length > 0,
                        }),
                    );
            });
        }
    }, [JSON.stringify(currentResults.validation)]);

    return (
        <Grid container style={{ alignItems: 'center' }} className='style-form'>
            <Dialog open={!!ErrorDialog && ErrorDialog.length > 0} title={`${t('errors.generic.title')}`} onClose={closeErrorDialog}>
                <>
                    <div>{ErrorDialog}</div>
                </>
            </Dialog>
            <FormikProvider value={formikProps}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('crest_rope_layout')}</label>
                    </Grid>
                    <Grid item xs={11}>
                        <div className='d-flex'>
                            <label>{t('crest_horizontal_spacing', { unit_measure: getMeasurementUnit(conversionLabel.m_ft) })}</label>
                            <i
                                onClick={() => {
                                    openDialog('anchor_spacing');
                                }}
                                className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon'
                            />
                        </div>
                        <FieldNumber step='0.01' name='horizontalAnchorSpacing' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='horizontalAnchorSpacing' component='span' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('crest_symbols.crest_horizontal_spacing')}</label>
                    </div>

                    {/*<Grid item xs={11}>
                        <div className="d-flex">
                            <label>{t('crest_vertical_offset', { unit_measure: getMeasurementUnit(conversionLabel.m_ft) })}</label>
                            <i onClick={() => { openDialog('anchor_spacing') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
                        </div>
                        <FieldNumber name='verticalAnchorSpacing' className='d-flex justify-content-between align-items-center' />
                        <ErrorMessage name='verticalAnchorSpacing' component='span' className="text-danger" />
                    </Grid>
                    <Grid xs={1}>
                        <label className='symbol'>{t('crest_symbols.crest_vertical_offset')}</label>
                    </Grid>*/}
                    <Dialog open={CurrentDialog == 'anchor_spacing'} title={`${t('crest_dialog_spacing')}`} onClose={closeDialog}>
                        <>
                            <div>
                                <img
                                    width='100%'
                                    height='100%'
                                    srcSet={`${anchorSpacing}?fit=crop&auto=format&dpr=2 2x`}
                                    src={`${anchorSpacing}?fit=crop&auto=format&dpr=2 2x`}
                                    alt='Anchor Spacing'></img>
                            </div>
                        </>
                    </Dialog>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('crest_rope_specification')}</label>
                    </Grid>
                    <Grid item xs={12}>
                        <label>{t('crest_rope_custom')}</label>
                        <Field type='checkbox' name='customRope' onChange={(e: React.ChangeEvent<any>) => handleValueChanged('customRope', e.target.checked)} disabled={props.readOnly} />
                    </Grid>
                    {formikProps.values.customRope ? (
                        <>
                            <Grid item xs={11}>
                                <label>{t('crest_rope_diameter', { unit_measure: getMeasurementUnit(conversionLabel.mm_in) })}</label>
                                <FieldNumber step='0.01' name='ropeDiameter' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                                <ErrorMessage name='ropeDiameter' component='span' className='text-danger' />
                            </Grid>
                            <div className='symbol-wrap'>
                                <label className='symbol'>{t('crest_symbols.crest_rope_diameter')}</label>
                            </div>
                            <Grid item xs={11}>
                                <label>{t('crest_rope_steel_grade', { unit_measure: getMeasurementUnit(conversionLabel.mpa_mpa) })}</label>
                                <FieldNumber step='0.01' name='ropeSteelGrade' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                                <ErrorMessage name='ropeSteelGrade' component='span' className='text-danger' />
                            </Grid>
                            <div className='symbol-wrap'>
                                <label className='symbol'>{t('crest_symbols.crest_rope_steel_grade')}</label>
                            </div>
                        </>
                    ) : (
                        <>
                            <Grid item xs={11}>
                                <label>{t('crest_rope_diameter', { unit_measure: getMeasurementUnit(conversionLabel.mm_in) })}</label>
                                <Field
                                    as={MuiSelect}
                                    name='ropeDiameter'
                                    value={formikProps.values.ropeDiameterId}
                                    onChange={(e: any) => handleValueChanged('ropeDiameterId', e.target.value)}
                                    disabled={props.readOnly}>
                                    {RopesTypes.map(mt => {
                                        return (
                                            <MenuItem key={mt.id} value={mt.id}>
                                                {mt.diameter}
                                            </MenuItem>
                                        );
                                    })}
                                </Field>
                                <ErrorMessage name='ropeDiameter' component='span' className='text-danger' />
                            </Grid>
                            <div className='symbol-wrap'>
                                <label className='symbol'>{t('crest_symbols.crest_rope_diameter')}</label>
                            </div>
                            <Grid item xs={11}>
                                <label>{t('crest_rope_steel_grade', { unit_measure: getMeasurementUnit(conversionLabel.mpa_mpa) })}</label>
                                <Field
                                    as={MuiSelect}
                                    name='ropeSteelGrade'
                                    value={formikProps.values.ropeSteelGradeId}
                                    onChange={(e: any) => handleValueChanged('ropeSteelGradeId', e.target.value)}
                                    disabled={props.readOnly}>
                                    {RopeSteelsTypes.map(mt => {
                                        return (
                                            <MenuItem key={mt.id} value={mt.id}>
                                                {mt.grade}
                                            </MenuItem>
                                        );
                                    })}
                                </Field>
                                <ErrorMessage name='ropeSteelGrade' component='span' className='text-danger' />
                            </Grid>
                            <div className='symbol-wrap'>
                                <label className='symbol'>{t('crest_symbols.crest_rope_steel_grade')}</label>
                            </div>
                        </>
                    )}
                    <Grid item xs={12}>
                        <label>{t('crest_rope_core_type')}</label>
                        {formikProps.values.customRope ? (
                            <Field type='text' name='ropeCoreTypeName' value={formikProps.values.ropeCoreTypeName} disabled={props.readOnly}></Field>
                        ) : (
                            <Field
                                as={MuiSelect}
                                name='ropeCoreType'
                                value={formikProps.values.ropeCoreType}
                                onChange={(e: any) => handleValueChanged('ropeCoreType', e.target.value)}
                                disabled={props.readOnly}>
                                {RopeCoresTypes.map(mt => {
                                    return (
                                        <MenuItem key={mt.id} value={mt.id}>
                                            {mt.typeName}
                                        </MenuItem>
                                    );
                                })}
                            </Field>
                        )}
                        <ErrorMessage name='ropeCoreType' component='span' className='text-danger' />
                    </Grid>
                    <Grid item xs={11}>
                        <label>{t('crest_rope_tensile_strength', { unit_measure: getMeasurementUnit(conversionLabel.kN_lbf) })}</label>
                        <FieldNumber
                            step='0.01'
                            name='ropeUltimateTensileStrength'
                            className='d-flex justify-content-between align-items-center'
                            disabled={props.readOnly ? true : !formikProps.values.customRope}
                        />
                        <ErrorMessage name='ropeUltimateTensileStrength' component='span' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('crest_symbols.crest_rope_tensile_strength')}</label>
                    </div>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('crest_anchor_specification')}</label>
                    </Grid>
                    <Grid item xs={12}>
                        <label>{t('crest_anchor_custom')}</label>
                        <Field
                            type='checkbox'
                            name='customAnchor'
                            className='d-flex justify-content-between align-items-center'
                            onChange={(e: React.ChangeEvent<any>) => handleValueChanged('customAnchor', e.target.checked)}
                            disabled={props.readOnly}
                        />
                        <ErrorMessage name='customAnchor' component='span' className='text-danger' />
                    </Grid>
                    <Grid item xs={12}>
                        <label>{t('crest_anchor_type')}</label>
                        {formikProps.values.customAnchor ? (
                            <Field type='text' name='anchorTypeName' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        ) : (
                            <Field
                                as={MuiSelect}
                                name='anchorType'
                                value={formikProps.values.anchorType}
                                onChange={(e: any) => handleValueChanged('anchorType', e.target.value)}
                                disabled={props.readOnly}>
                                {AnchorTypes.map(mt => {
                                    return (
                                        <MenuItem key={mt.id} value={mt.id}>
                                            {mt.typeName}
                                        </MenuItem>
                                    );
                                })}
                            </Field>
                        )}
                        <ErrorMessage name='anchorType' component='span' className='text-danger' />
                    </Grid>
                    <Grid item xs={11}>
                        <label>{t('crest_inner_diameter', { unit_measure: getMeasurementUnit(conversionLabel.mm_in) })}</label>
                        <FieldNumber
                            step='0.01'
                            name='anchorInternalDiameter'
                            disabled={props.readOnly ? true : !formikProps.values.customAnchor}
                            className='d-flex justify-content-between align-items-center'
                        />
                        <ErrorMessage name='anchorInternalDiameter' component='span' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('crest_symbols.crest_inner_diameter')}</label>
                    </div>
                    <Grid item xs={11}>
                        <label>{t('crest_external_diameter', { unit_measure: getMeasurementUnit(conversionLabel.mm_in) })}</label>
                        {formikProps.values.customAnchor ? (
                            <FieldNumber step='0.01' name='anchorExternalDiameter' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        ) : (
                            <Field
                                as={MuiSelect}
                                name='anchorExternalDiameter'
                                value={formikProps.values.anchorExternalDiameter}
                                onChange={(e: any) => handleValueChanged('anchorExternalDiameter', e.target.value)}
                                disabled={props.readOnly}>
                                {AnchorExternalDiameters.map(mt => {
                                    return (
                                        <MenuItem key={mt} value={mt}>
                                            {mt}
                                        </MenuItem>
                                    );
                                })}
                            </Field>
                        )}
                        <ErrorMessage name='anchorExternalDiameter' component='span' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('crest_symbols.crest_external_diameter')}</label>
                    </div>
                    <Grid item xs={11}>
                        <label>{t('crest_drilling_diameter', { unit_measure: getMeasurementUnit(conversionLabel.mm_in) })}</label>
                        <FieldNumber step='0.01' name='drillingDiameter' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='drillingDiameter' component='span' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('crest_symbols.crest_drilling_diameter')}</label>
                    </div>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('crest_corrosion_crown')}</label>
                    </Grid>
                    {/*<Grid item xs={12}>
                        <label>{t('crest_automatic_thickness', { unit_measure: getMeasurementUnit(conversionLabel.mm_in) })}</label>
                        <Field type='checkbox' name='automaticCrownThickness' className='d-flex justify-content-between align-items-center' />
                        <ErrorMessage name='automaticCrownThickness' component='span' className="text-danger" />
                    </Grid>*/}
                    <Grid item xs={11}>
                        <label>{t('crest_crown_thickness', { unit_measure: getMeasurementUnit(conversionLabel.mm_in) })}</label>
                        <FieldNumber
                            step='0.01'
                            name='crownThickness'
                            className='d-flex justify-content-between align-items-center'
                            disabled={props.readOnly ? true : formikProps.values.automaticCrownThickness}
                        />
                        <ErrorMessage name='crownThickness' component='span' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('anchor_bar_symbols.anchor_bar_custom_thickness')}</label>
                    </div>
                    {formikProps.values.automaticCrownThickness ? (
                        <>
                            <Grid item xs={12} style={{ paddingLeft: 48 }}>
                                <label>{t('crest_environmental_aggressivity', { unit_measure: getMeasurementUnit(conversionLabel.mmy_iny) })}</label>
                                <Field
                                    as={MuiSelect}
                                    name='environmentalAggressivity'
                                    value={formikProps.values.environmentalAggressivity}
                                    onChange={(e: any) => handleValueChanged('environmentalAggressivity', e.target.value)}
                                    disabled={props.readOnly}>
                                    <MenuItem value={25}>{t('crest_environmental_aggressivity_options.low')}</MenuItem>
                                    <MenuItem value={37.5}>{t('crest_environmental_aggressivity_options.medium')}</MenuItem>
                                    <MenuItem value={50}>{t('crest_environmental_aggressivity_options.hard')}</MenuItem>
                                </Field>
                            </Grid>
                            <Grid item xs={12} style={{ paddingLeft: 48 }}>
                                <label>{t('crest_lifespan', { unit_measure: getMeasurementUnit(conversionLabel.years) })}</label>
                                <Field type='text' name='lifespanYears' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                                <ErrorMessage name='lifespanYears' component='span' className='text-danger' />
                            </Grid>
                        </>
                    ) : (
                        <></>
                    )}
                    <Grid item xs={12} style={{ paddingLeft: 48 }}>
                        <Divider />
                    </Grid>
                    <Grid item xs={11}>
                        <label>{t('crest_anchor_yield_stress', { unit_measure: getMeasurementUnit(conversionLabel.mpa_ksi) })}</label>
                        <FieldNumber
                            step='0.01'
                            name='anchoryieldStress'
                            disabled={props.readOnly ? true : !formikProps.values.customAnchor}
                            className='d-flex justify-content-between align-items-center'
                        />
                        <ErrorMessage name='anchoryieldStress' component='span' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('crest_symbols.crest_anchor_yield_stress')}</label>
                    </div>
                    {/* <Grid item xs={12}>
                        <label>{t('crest_anchor_NFP94270')}</label>
                        <Field type="checkbox" name="NFP94270" />
                        <ErrorMessage name='NFP94270' component='span' className="text-danger" />
                </Grid> */}
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('crest_bond_stress')}</label>
                    </Grid>
                    <Grid item xs={11}>
                        <div className='d-flex'>
                            <label>{t('crest_rock_grout_adhesion', { unit_measure: getMeasurementUnit(conversionLabel.mpa_psi) })}</label>
                            <i
                                onClick={() => {
                                    openDialog('rock_grout');
                                }}
                                className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon'
                            />
                        </div>
                        <FieldNumber step='0.01' name='rockGroutAdhesion' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='rockGroutAdhesion' component='span' className='text-danger' />
                    </Grid>
                    <Dialog open={CurrentDialog == 'rock_grout'} title={`${t('crest_dialog_rock_grout_adhesion')}`} onClose={closeDialog}>
                        <>
                            <div>
                                <img
                                    width='100%'
                                    height='100%'
                                    srcSet={`${rockGroutSrc}?fit=crop&auto=format&dpr=2 2x`}
                                    src={`${rockGroutSrc}?fit=crop&auto=format&dpr=2 2x`}
                                    alt='Anchor Spacing'></img>
                            </div>
                        </>
                    </Dialog>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('crest_symbols.crest_rock_grout_adhesion')}</label>
                    </div>
                    <Grid item xs={11}>
                        <div className='d-flex'>
                            <label>{t('crest_assumed_plasticization', { unit_measure: getMeasurementUnit(conversionLabel.m_ft) })}</label>
                            <i
                                onClick={() => {
                                    openDialog('assumed_plasticization');
                                }}
                                className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon'
                            />
                        </div>
                        <FieldNumber step='0.01' name='assumedPlasticizationBetweenRockAndAnchor' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='assumedPlasticizationBetweenRockAndAnchor' component='span' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('anchor_bar_symbols.anchor_bar_assumed_plasticization')}</label>
                    </div>
                    <Dialog open={CurrentDialog == 'assumed_plasticization'} title={`${t('crest_dialog_assumed_plasticization')}`} onClose={closeDialog}>
                        <>
                            <div>
                                <img
                                    width='100%'
                                    height='100%'
                                    srcSet={`${assumedPlasticization}?fit=crop&auto=format&dpr=2 2x`}
                                    src={`${assumedPlasticization}?fit=crop&auto=format&dpr=2 2x`}
                                    alt='Anchor Spacing'></img>
                            </div>
                        </>
                    </Dialog>
                </Grid>
            </FormikProvider>
        </Grid>
    );
};

export default Crest;
